// frontend/src/components/parent/ParentStatsGrid.tsx
// Keskin, kurumsal ve yüksek hassasiyetli KPI gösterge kartları

import { ActivityReport, ChildOverview } from '../../types/parent.types';
import {
  IconBook,
  IconTarget,
  IconFolder,
  IconMessageCircle,
  IconFlame,
  IconStar,
  IconActivity,
  IconClock,
} from './ParentIcons';

interface StatCardProps {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  value: string | number;
  label: string;
  sublabel?: string;
  trend?: string;
}

function StatCard({ icon, iconBg, iconColor, value, label, sublabel, trend }: StatCardProps) {
  return (
    <div className="pcd-stat-card pcd-sharp-stat">
      <div className="pcd-stat-icon-wrap" style={{ background: iconBg }}>
        <span style={{ color: iconColor }}>{icon}</span>
      </div>
      <div className="pcd-stat-body">
        <div className="pcd-stat-value">{value}</div>
        <div className="pcd-stat-label">{label}</div>
        {sublabel && <div className="pcd-stat-sublabel">{sublabel}</div>}
      </div>
      {trend && <div className="pcd-stat-trend">{trend}</div>}
    </div>
  );
}

interface ParentStatsGridProps {
  report: ActivityReport | null;
  overview?: ChildOverview | null;
  isLoading: boolean;
}

export function ParentStatsGrid({ report, overview, isLoading }: ParentStatsGridProps) {
  if (isLoading) {
    return (
      <div className="pcd-stats-grid">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="pcd-stat-card pcd-skeleton" />
        ))}
      </div>
    );
  }

  // Çalışma süresi formatı
  const studyMins = report?.studyMinutes ?? (overview ? Math.round(overview.totalStudyMinutes) : 0);
  const studyHoursText = studyMins >= 60
    ? `${Math.floor(studyMins / 60)} sa ${studyMins % 60} dk`
    : `${studyMins} dk`;

  const stats: StatCardProps[] = [
    {
      icon: <IconBook size={20} />,
      iconBg: 'rgba(14,165,233,0.12)',
      iconColor: '#0EA5E9',
      value: report?.lessonsCompleted ?? overview?.completedLessonsCount ?? 0,
      label: 'Tamamlanan Ders',
      sublabel: report ? `${report.label} dönemi` : 'Toplam müfredat',
    },
    {
      icon: <IconTarget size={20} />,
      iconBg: 'rgba(239,68,68,0.12)',
      iconColor: '#EF4444',
      value: report?.quizzesSolved ?? overview?.totalQuizzes ?? 0,
      label: 'Çözülen Quiz',
      sublabel: report?.averageQuizScore ? `Ort. %${report.averageQuizScore} başarı` : overview ? `Ort. %${overview.averageQuizScore}` : undefined,
    },
    {
      icon: <IconClock size={20} />,
      iconBg: 'rgba(16,185,129,0.12)',
      iconColor: '#10B981',
      value: studyHoursText,
      label: 'Çalışma Süresi',
      sublabel: 'Aktif ders & pratik',
    },
    {
      icon: <IconFolder size={20} />,
      iconBg: 'rgba(245,158,11,0.12)',
      iconColor: '#F59E0B',
      value: report?.projectsSubmitted ?? overview?.totalProjects ?? 0,
      label: 'Tamamlanan Proje',
      sublabel: 'Kodlama uygulaması',
    },
    {
      icon: <IconMessageCircle size={20} />,
      iconBg: 'rgba(139,92,246,0.12)',
      iconColor: '#8B5CF6',
      value: report?.aiChats ?? overview?.totalAiQuestions ?? 0,
      label: 'AI Mentor Danışma',
      sublabel: 'Teknik soru & analiz',
    },
    {
      icon: <IconActivity size={20} />,
      iconBg: 'rgba(6,182,212,0.12)',
      iconColor: '#06B6D4',
      value: report ? `%${report.averageQuizScore}` : overview ? `%${overview.averageQuizScore}` : '—',
      label: 'Genel Sınav Skoru',
      sublabel: 'Ağırlıklı başarı',
    },
    {
      icon: <IconFlame size={20} />,
      iconBg: 'rgba(249,115,22,0.12)',
      iconColor: '#F97316',
      value: `${report?.streakDays ?? overview?.profile.current_streak_days ?? 0} Gün`,
      label: 'Aktif Seri',
      sublabel: 'Kesintisiz katılım',
    },
    {
      icon: <IconStar size={20} />,
      iconBg: 'rgba(99,102,241,0.12)',
      iconColor: '#6366F1',
      value: (report?.xpEarned ?? overview?.profile.xp ?? 0).toLocaleString('tr-TR'),
      label: 'Kazanılan XP',
      sublabel: 'Akademik puan',
    },
  ];

  return (
    <div className="pcd-stats-grid">
      {stats.map((s, i) => (
        <StatCard key={i} {...s} />
      ))}
    </div>
  );
}
